import { useEffect, useRef, useState } from 'react';

/** Pause après la dernière frappe avant d'écrire dans le store Puck. */
export const FIELD_DEBOUNCE_MS = 400;

/**
 * La frappe d'un champ de sidebar, en local, écrite au store à la pause.
 *
 * Un champ Puck contrôlé dispatch un `replace` à CHAQUE frappe : re-résolution
 * et re-rendu du canvas, donc du lag sur les grandes pages (invariant de perf
 * §4.3 du README). Les trois champs débouncés du webviewer — texte, espacement,
 * largeur — écrivaient chacun le même mécanisme : état local, drapeau de focus,
 * minuteur, resynchronisation hors focus.
 *
 * `commit` peut changer d'identité à chaque rendu de la sidebar : il est lu
 * dans une ref, pour qu'un minuteur déjà armé appelle toujours le dernier.
 *
 * @param {*} value valeur du store
 * @param {(next: *) => void} commit écriture dans le store
 * @param {{ toLocal?: (v: *) => *, delay?: number }} options `toLocal`
 *   convertit la valeur stockée en valeur affichée (px ↔ rem, par exemple).
 */
export function useDebouncedFieldValue(value, commit, { toLocal, delay = FIELD_DEBOUNCE_MS } = {}) {
  const latest = useRef({ commit, toLocal });
  latest.current = { commit, toLocal };

  const read = (v) => (latest.current.toLocal ? latest.current.toLocal(v) : (v ?? ''));

  const [local, setLocal] = useState(() => read(value));
  const timerRef = useRef(null);
  // Frappe pas encore écrite : seule chose qu'un flush a le droit d'écrire.
  const pendingRef = useRef(null);
  const focusedRef = useRef(false);

  // Changement externe (undo/redo, restauration, rafraîchissement) : on
  // réaffiche, sauf pendant la frappe — sinon on écraserait ce qu'on tape.
  useEffect(() => {
    if (!focusedRef.current) setLocal(read(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const cancel = () => {
    clearTimeout(timerRef.current);
    pendingRef.current = null;
  };

  return {
    local,
    setLocal,
    /** Frappe : affichée tout de suite, écrite après la pause. */
    change: (next) => {
      setLocal(next);
      cancel();
      pendingRef.current = { next };
      timerRef.current = setTimeout(() => {
        pendingRef.current = null;
        latest.current.commit(next);
      }, delay);
    },
    /**
     * Écrit tout de suite la frappe en attente, s'il y en a une. Rien
     * d'autre : la valeur affichée peut être en retard d'un rendu sur un
     * changement externe (undo), la réécrire annulerait ce changement.
     */
    flushPending: () => {
      const pending = pendingRef.current;
      if (!pending) return;
      cancel();
      latest.current.commit(pending.next);
    },
    focus: () => { focusedRef.current = true; },
    /** Fin de frappe : au champ de décider ce qu'il écrit, et s'il écrit. */
    endFocus: () => {
      focusedRef.current = false;
      cancel();
    },
    cancel,
  };
}

export default useDebouncedFieldValue;
